import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import { useAppTranslation } from "locale/useAppTranslation";
import {
  useTripTypeServiceV1GetTripTypeQuery,
  useTripTypeServiceV1UpdateTripTypeMutation,
} from "_services/modules/trip_type";
import { toast } from "react-toastify";
import { errorHandler } from "_services/errorHandler";

interface EditTripTypeFormValues {
  tripTypeName: string;
  description: string;
  isFreezer: boolean;
}

const schema = yup.object({
  tripTypeName: yup.string().required("Trip Type Name is required").max(100, "Max 100 characters"),
  description: yup.string().max(500, "Max 500 characters").default(""),
  isFreezer: yup.boolean().default(false),
}).required();

const useEditTripTypeForm = (id: string | undefined) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useAppTranslation("tripType");

  const { data: detail, isLoading } = useTripTypeServiceV1GetTripTypeQuery(
    { id: id! },
    { skip: !id }
  );

  const [updateTripType] = useTripTypeServiceV1UpdateTripTypeMutation();

  const {
    handleSubmit,
    formState: { errors },
    register,
    setError,
    reset,
    control,
  } = useForm<EditTripTypeFormValues>({
    mode: "onBlur",
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (detail?.trip_type_name) {
      reset({
        tripTypeName: detail.trip_type_name,
        description: detail.description ?? "",
        isFreezer: detail.is_freezer ?? false,
      });
    }
  }, [detail, reset]);

  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [pendingData, setPendingData] = useState<EditTripTypeFormValues | null>(null);

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: t("page-title-edit"),
        hasBackButton: true,
      })
    );
  }, [dispatch, t]);

  const onSubmit = (data: EditTripTypeFormValues) => {
    setPendingData(data);
    setShowSaveModal(true);
  };

  const handleConfirmSave = async () => {
    if (!pendingData || !id) return;
    try {
      await updateTripType({
        id,
        tripTypeServiceV1UpdateTripTypeBody: {
          trip_type_name: pendingData.tripTypeName,
          description: pendingData.description,
          is_freezer: pendingData.isFreezer,
        },
      }).unwrap();
      toast.success(t("toast-update-success"));
      setShowSaveModal(false);
      navigate(-1);
    } catch (error: any) {
      if (error?.status === 409) {
        setError("tripTypeName", {
          type: "validate",
          message: t("error-duplicate-exists"),
        });
        setShowSaveModal(false);
      } else if (error?.status === 404) {
        setShowSaveModal(false);
        navigate(-1);
      } else {
        errorHandler(error);
        setShowSaveModal(false);
      }
    }
  };

  const handleConfirmCancel = () => {
    setShowCancelModal(false);
    navigate(-1);
  };

  return {
    handleSubmit,
    errors,
    register,
    setError,
    control,
    showSaveModal,
    setShowSaveModal,
    showCancelModal,
    setShowCancelModal,
    onSubmit,
    handleConfirmSave,
    handleConfirmCancel,
    isLoading,
    t,
  };
};

export default useEditTripTypeForm;
